// assets/product-custom-property.js
import { Component } from '@theme/component';

/**
 * @typedef {object} ProductCustomPropertyRefs
 * @property {HTMLInputElement | HTMLTextAreaElement} textInput - The text input.
 * @property {HTMLElement} characterCount - The character count element.
 */

/**
 * A custom element that manages product custom properties
 * @extends Component<ProductCustomPropertyRefs>
 */
class ProductCustomProperty extends Component {
  #artworkChanged = (event) => {
    if (event.target instanceof HTMLInputElement) this.#updateArtworkFilename(event.target);
  };

  #clockImageChanged = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.name.startsWith('Image-')) {
      this.#syncClockArtwork();
    }
  };

  #ashtrayColourChanged = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.name.startsWith('Colour-')) {
      this.#syncAshtrayInstructions();
    }
  };

  connectedCallback() {
    super.connectedCallback();
    const artworkInput = this.querySelector('input[type="file"]');
    if (artworkInput instanceof HTMLInputElement) {
      artworkInput.addEventListener('change', this.#artworkChanged);
      this.#updateArtworkFilename(artworkInput);
    }
    if (this.querySelector('[data-ashtray-instructions]')) {
      document.addEventListener('change', this.#ashtrayColourChanged);
      this.#syncAshtrayInstructions();
    }
    if (!this.querySelector('[data-clock-artwork]')) return;
    document.addEventListener('change', this.#clockImageChanged);
    this.#syncClockArtwork();
  }

  disconnectedCallback() {
    this.querySelector('input[type="file"]')?.removeEventListener('change', this.#artworkChanged);
    document.removeEventListener('change', this.#clockImageChanged);
    document.removeEventListener('change', this.#ashtrayColourChanged);
    super.disconnectedCallback?.();
  }

  #updateArtworkFilename(input) {
    const filename = this.querySelector('[data-upload-filename]');
    if (!(filename instanceof HTMLElement)) return;
    filename.textContent = input.files?.[0]?.name || filename.dataset.emptyText || '';
  }

  #syncClockArtwork() {
    const field = this.querySelector('[data-clock-artwork]');
    if (!(field instanceof HTMLElement)) return;

    const picker = this.closest('main')?.querySelector(`variant-picker[data-product-id="${field.dataset.productId}"]`);
    const imageChoice = picker?.querySelector('input[name^="Image-"]:checked');
    const enabled = imageChoice instanceof HTMLInputElement && imageChoice.value === 'with Image';
    const input = field.querySelector('input[type="file"]');

    field.hidden = !enabled;
    if (input instanceof HTMLInputElement) {
      input.disabled = !enabled;
      if (!enabled && input.value) {
        input.value = '';
        this.#updateArtworkFilename(input);
      }
    }
  }

  #syncAshtrayInstructions() {
    const field = this.querySelector('[data-ashtray-instructions]');
    if (!(field instanceof HTMLElement)) return;

    const picker = this.closest('main')?.querySelector(`variant-picker[data-product-id="${field.dataset.productId}"]`);
    const colourChoice = picker?.querySelector('input[name^="Colour-"]:checked');
    const instructions = field.querySelector('textarea, input[type="text"]');
    if (!(instructions instanceof HTMLInputElement || instructions instanceof HTMLTextAreaElement)) return;

    instructions.required = colourChoice instanceof HTMLInputElement && colourChoice.value === 'Custom colour';
    if (instructions.required) instructions.setAttribute('aria-required', 'true');
    else instructions.removeAttribute('aria-required');
  }

  handleInput() {
    this.#updateCharacterCount();
  }

  #updateCharacterCount() {
    const { characterCount, textInput } = this.refs;
    const currentLength = textInput.value.length;
    const maxLength = textInput.maxLength;

    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    const updatedText = template.replace('[current]', currentLength.toString()).replace('[max]', maxLength.toString());

    characterCount.textContent = updatedText;
  }
}

customElements.define('product-custom-property-component', ProductCustomProperty);
